import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { saveBlob } from '@/shared/lib/saveBlob';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import { FormField } from '@/shared/ui/FormField';
import { earnBonusByPhone, fetchEarnBonusReport } from './api';

const REPORT_FILE_NAME = 'export.csv';

const initialForm = { amount: '', comment: '', burned: true, onlyCustomer: true };

export const AccrualPointsPage = () => {
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState<File | null>(null);
  const [isDone, setIsDone] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const { errors, clearErrors, save, isPending } = useSaveForm(earnBonusByPhone, () => {
    setForm(initialForm);
    setFile(null);
    setIsDone(true);
  });

  const report = useMutation({
    mutationFn: fetchEarnBonusReport,
    onSuccess: (blob) => saveBlob(blob, REPORT_FILE_NAME),
  });

  const setField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setIsDone(false);
    clearErrors(key);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const selectFile = (selected: File | null) => {
    setIsDone(false);
    clearErrors();
    setFile(selected);
    if (!selected && fileInput.current) fileInput.current.value = '';
  };

  const onSubmit = () => {
    if (file) save({ file, ...form });
  };

  const { comment: commentErrors, amount: amountErrors, ...otherErrors } = errors;
  const otherMessages = Object.values(otherErrors).flat();

  return (
    <>
      <section className="content-header">
        <h1>Начисление баллов</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-body" style={{ padding: '31px 24px' }}>
            <h4 style={{ fontWeight: 600 }}>Начисление баллов по номеру телефона</h4>

            {isDone && <div className="alert alert-success">Баллы успешно начислены!</div>}

            <div className="form-group">
              <label className="btn btn-primary" htmlFor="accrual-file">
                <i className="fa fa-upload" /> Загрузить файл
              </label>
              <input
                ref={fileInput}
                id="accrual-file"
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={(e) => selectFile(e.target.files?.[0] ?? null)}
              />
              <span style={{ marginLeft: 12 }}>
                {file ? file.name : 'Файл не выбран'}
                {file && (
                  <a
                    href="#"
                    className="text-red"
                    style={{ marginLeft: 7 }}
                    onClick={(e) => {
                      e.preventDefault();
                      selectFile(null);
                    }}
                  >
                    X
                  </a>
                )}
              </span>
              {otherMessages.map((message) => (
                <div key={message} className="text-red">
                  {message}
                </div>
              ))}
            </div>

            <div className="checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={form.burned}
                  onChange={(e) => setField('burned', e.target.checked)}
                />
                Сгораемые
              </label>
            </div>
            <div className="checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={form.onlyCustomer}
                  onChange={(e) => setField('onlyCustomer', e.target.checked)}
                />
                Только кастомеры
              </label>
            </div>

            <FormField label="Комментарий" errors={commentErrors}>
              <input
                className="form-control"
                placeholder="Введите комментарий"
                value={form.comment}
                onChange={(e) => setField('comment', e.target.value)}
              />
            </FormField>

            <FormField label="Количество баллов" errors={amountErrors}>
              <input
                className="form-control input-sm"
                style={{ width: 250 }}
                placeholder="Баллы"
                value={form.amount}
                onChange={(e) => setField('amount', e.target.value.replace(/\D/g, ''))}
              />
            </FormField>

            <button type="button" className="btn btn-warning" disabled={!file || isPending} onClick={onSubmit}>
              Выполнить
            </button>{' '}
            <button
              type="button"
              className="btn btn-primary"
              disabled={report.isPending}
              onClick={() => report.mutate()}
            >
              Выгрузить результат
            </button>
            {report.isError && <p className="text-red">Не удалось выгрузить результат</p>}
          </div>
        </div>
      </section>
    </>
  );
};
